import { useCallback } from "react";
import { useLayoutRecorteExtracaoApi } from "../../../infra/geo-veles";
import { useNavigate } from "react-router-dom";
import {
  useAppSelector,
  useAppDispatch,
} from "../../../presentation/hooks/store-hooks";
import { useUIService } from "../../ui";
import {
  setLayoutsRecorteState,
  setRecorteExtracaoState,
} from "../../../application/states/geo-veles";
import {
  ILayoutRecorteTable,
  IRecorteExtracao,
} from "../../../application/models";

import {
  setCreateLayoutRecorteFormOpenState,
  setCreateLayoutRecorteFormFieldsNomeState,
  resetCreateLayoutRecorteFormNomeState,
  resetCreateRecorteFormState,
  resetLayoutRecorteSelecionadoState,
  setEditLayoutRecorteFormOpenState,
  setLayoutRecorteSelecionadoState,
} from "../../../application/states/geo-veles";
import { useRecortesExtracaoService } from "../recortes-extracao";

export const useLayoutRecorteExtracaoService = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { projetoAberto } = useAppSelector((state) => state.projetos);
  const { currentUser } = useAppSelector((state) => state.user);

  const { layoutRecorteSelecionado, Layouts, createLayoutRecorteForm } =
    useAppSelector((state) => state.layoutRecorte);

  const { recorteExtracao } = useAppSelector((state) => state.recorteExtracao);

  const {
    createLayouRecortetApi,
    getLayoutsRecorteByIdUsuarioApi,
    updateLayoutRecorteApi,
  } = useLayoutRecorteExtracaoApi();

  const { setAlert } = useUIService();

  const { resetRecorteExtracao } = useRecortesExtracaoService();

  const setCreateLayoutRecorteFormOpen = useCallback(
    (open: boolean) => {
      dispatch(setCreateLayoutRecorteFormOpenState(open));
      navigate(
        `/geo-veles/projetos/${projetoAberto?.IdVelesGeoProjeto}/recorte/createlayoutRecorteExtracao`
      );
    },
    [dispatch, navigate, projetoAberto?.IdVelesGeoProjeto]
  );

  const setCreateLayoutRecorteFormNome = useCallback(
    (Nome: string) => {
      dispatch(setCreateLayoutRecorteFormFieldsNomeState(Nome));
    },
    [dispatch]
  );

  const resetLayoutSelecionado = useCallback(() => {
    dispatch(resetLayoutRecorteSelecionadoState());
  }, [dispatch]);

  const setEditLayoutRecorteFormOpen = useCallback(
    (data: { open: boolean; layoutSelecionado: ILayoutRecorteTable }) => {
      dispatch(setEditLayoutRecorteFormOpenState(data.open));
      navigate(
        `/geo-veles/projetos/${layoutRecorteSelecionado?.IdVelesGeoLayoutRecorte}/recorte/editlayoutRecorteExtracao`
      );
    },
    [dispatch, layoutRecorteSelecionado?.IdVelesGeoLayoutRecorte, navigate]
  );

  const createLayoutRecorteExtracao = useCallback(async () => {
    try {
      dispatch(resetLayoutRecorteSelecionadoState());

      const layoutModelo: ILayoutRecorteTable = {
        NomeLayout: createLayoutRecorteForm.fields.Nome,
        Recortes: recorteExtracao.Recortes,
        IdUsuario: currentUser?.IdUsuario ?? 0,
        NomeUsuario: currentUser?.Nome ?? "",
        LoginUsuario: currentUser?.LoginUsuario ?? "",
        Inclusao: new Date(),
        Atualizacao: null,
        IdVelesGeoLayoutRecorte: 0,
        Ativo: true,
      };

      const createLayoutRecorteExtracao = await createLayouRecortetApi({
        layout: layoutModelo?.Recortes ?? [],
        IdUsuario: layoutModelo.IdUsuario,
        NomeLayout: layoutModelo.NomeLayout
      });
      if (createLayoutRecorteExtracao) {
        dispatch(resetCreateRecorteFormState());
        dispatch(resetCreateLayoutRecorteFormNomeState());
        setAlert({
          message: "Layout criado com sucesso!",
          type: "success",
        });

        // Redireciona e depois recarrega a página
        navigate(
          `/geo-veles/projetos/${projetoAberto?.IdVelesGeoProjeto}/recorte`
        );
      }
    } catch (error: any) {
      setAlert({
        message:
          error.response?.data?.error || "Erro ao processar a requisição",
        type: "error",
      });
    }
  }, [
    createLayoutRecorteForm.fields.Nome,
    recorteExtracao,
    currentUser?.IdUsuario,
    currentUser?.Nome,
    currentUser?.LoginUsuario,
    dispatch,
    layoutRecorteSelecionado,
    createLayouRecortetApi,
    setAlert,
    navigate,
    projetoAberto?.IdVelesGeoProjeto,
  ]);

  const getLayoutsRecorteByIdUsuario = useCallback(
    async (IdUsuario: number) => {
      try {
        const getLayoutsRecorteByIdUsuario =
          await getLayoutsRecorteByIdUsuarioApi(IdUsuario);
        if (getLayoutsRecorteByIdUsuario) {
          dispatch(setLayoutsRecorteState(getLayoutsRecorteByIdUsuario));
        }
      } catch (error: any) {
        setAlert({
          message: error.response?.data?.error || "Erro ao trazer os layouts",
          type: "error",
        });
      }
    },
    [dispatch, getLayoutsRecorteByIdUsuarioApi, setAlert]
  );

  const setLayoutRecorteSelecionado = useCallback(
    (data: { layout: ILayoutRecorteTable; idVelesGeoProjeto: number }) => {
      const recortesLayout: ILayoutRecorteTable = {
        IdVelesGeoLayoutRecorte: data.layout.IdVelesGeoLayoutRecorte,
        IdUsuario: data.layout.IdUsuario,
        NomeLayout: data.layout.NomeLayout,
        Recortes: data.layout.Recortes,
        Inclusao: data.layout.Inclusao,
        Atualizacao: data.layout.Atualizacao,
        Ativo: data.layout.Ativo,
        // LoginUsuario: data.layout.LoginUsuario,
        // NomeUsuario: data.layout.NomeUsuario,
          LoginUsuario: currentUser?.LoginUsuario?? '',
        NomeUsuario: currentUser?.Nome?? '',
      };

      dispatch(setLayoutRecorteSelecionadoState(recortesLayout));

      if (recortesLayout) {
        resetRecorteExtracao();
        dispatch(setLayoutsRecorteState(Layouts));
        dispatch(
          setRecorteExtracaoState({
            IdVelesGeoRecorteExtracao: 0,
            IdVelesGeoProjeto: projetoAberto?.IdVelesGeoProjeto ?? 0,
            Inclusao: data.layout.Inclusao,
            Recortes: data.layout.Recortes,
            Atualizacao: data.layout.Atualizacao,
          })
        );
      }
      getLayoutsRecorteByIdUsuario(currentUser?.IdUsuario ?? 0);
    },
    [dispatch, resetRecorteExtracao]
  );

  const updateLayoutRecorte = useCallback(
    async (data: { layout: IRecorteExtracao; NomeLayout: string }) => {
      try {
        const updateLayoutRecorte = await updateLayoutRecorteApi({
          IdLayoutRecorte:
            layoutRecorteSelecionado?.IdVelesGeoLayoutRecorte ?? 0,
          NomeLayout: data.NomeLayout,
          Recortes: data.layout.Recortes,
        });
        if (updateLayoutRecorte) {
          setAlert({
            message: "Layout editado com sucesso!",
            type: "success",
          });

          // Redireciona e depois recarrega a página
          navigate(
            `/geo-veles/projetos/${projetoAberto?.IdVelesGeoProjeto}/recorte`
          );
        }
      } catch (error: any) {
        setAlert({
          message:
            error.response?.data?.error || "Erro ao processar a requisição",
          type: "error",
        });
      }
    },
    [
      updateLayoutRecorteApi,
      layoutRecorteSelecionado?.IdVelesGeoLayoutRecorte,
      setAlert,
      navigate,
      projetoAberto?.IdVelesGeoProjeto,
    ]
  );

  return {
    setCreateLayoutRecorteFormOpen,
    createLayoutRecorteExtracao,
    setCreateLayoutRecorteFormNome,
    getLayoutsRecorteByIdUsuario,
    setLayoutRecorteSelecionado,
    resetLayoutSelecionado,
    setEditLayoutRecorteFormOpen,
    updateLayoutRecorte,
  };
};
